import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Wallet,
  Calendar,
  Settings,
  X,
  Eye,
  Check,
  Filter
} from 'lucide-react';
import { formatBrandMoney, getBrandLocale } from '../utils/brandCurrency';

/**
 * Shared Alerts Page Component for all modules
 * Usage: <ModuleAlertsPage moduleName="Facturation" moduleIcon={Receipt} alerts={alertsData} />
 */
const ModuleAlertsPage = ({ moduleName = 'Module', moduleIcon: ModuleIcon = Bell, alerts = [] }) => {
  const { t, i18n } = useTranslation();
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const defaultAlerts = [
    {
      id: 1,
      type: 'warning',
      priority: 'high',
      title: t('modules.shared.alerts.default_attention_title', { moduleName }),
      message: t('modules.shared.alerts.default_attention_message', { moduleName }),
      date: new Date().toISOString().split('T')[0],
      read: false
    },
    {
      id: 2,
      type: 'info',
      priority: 'medium',
      title: t('modules.shared.alerts.default_info_title'),
      message: t('modules.shared.alerts.default_info_message', { moduleName }),
      date: new Date().toISOString().split('T')[0],
      read: true
    }
  ];

  const displayAlerts = alerts.length > 0 ? alerts : defaultAlerts;

  const getAlertIcon = (type) => {
    switch (type) {
      case 'low_balance': return { icon: Wallet, color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'payment_due': return { icon: Calendar, color: 'text-amber-400', bg: 'bg-amber-500/20' };
      case 'overdraft': return { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'warning': return { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/20' };
      case 'error': return { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'success': return { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20' };
      case 'info': return { icon: AlertCircle, color: 'text-blue-400', bg: 'bg-blue-500/20' };
      default: return { icon: Bell, color: 'text-gray-400', bg: 'bg-gray-500/20' };
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return { text: t('modules.shared.alerts.priority_critical'), color: 'bg-red-500/20 text-red-400 border-red-500/30' };
      case 'high':
        return { text: t('modules.shared.alerts.priority_high'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
      case 'medium':
        return { text: t('modules.shared.alerts.priority_medium'), color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
      case 'low':
        return { text: t('modules.shared.alerts.priority_low'), color: 'bg-green-500/20 text-green-400 border-green-500/30' };
      default:
        return { text: t('modules.shared.alerts.priority_info'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    }
  };

  const filteredAlerts = displayAlerts.filter(alert => {
    const matchesType = filterType === 'all' || alert.type === filterType;
    const matchesPriority = filterPriority === 'all' || alert.priority === filterPriority;
    return matchesType && matchesPriority;
  });

  const stats = {
    total: displayAlerts.length,
    unread: displayAlerts.filter(a => !a.read).length,
    critical: displayAlerts.filter(a => a.priority === 'critical').length,
    high: displayAlerts.filter(a => a.priority === 'high').length
  };

  const dateLocale = getBrandLocale(i18n.language);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-red-500 to-red-700">
              {ModuleIcon ? <ModuleIcon className="h-8 w-8" /> : <Bell className="h-8 w-8" />}
            </div>
            {t('modules.shared.alerts.title', { moduleName })}
          </h1>
          <p className="text-white/60 mt-2">{t('modules.shared.alerts.subtitle', { moduleName })}</p>
        </div>
        <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl flex items-center gap-2 border border-white/20">
          <Settings size={18} />
          {t('modules.shared.alerts.configuration')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600"><Bell className="h-6 w-6 text-white" /></div>
          </div>
          <p className="text-white/60 text-sm font-medium">{t('modules.shared.alerts.stat_total')}</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600"><AlertCircle className="h-6 w-6 text-white" /></div>
          </div>
          <p className="text-white/60 text-sm font-medium">{t('modules.shared.alerts.stat_unread')}</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{stats.unread}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-red-500 to-red-600"><AlertTriangle className="h-6 w-6 text-white" /></div>
          </div>
          <p className="text-white/60 text-sm font-medium">{t('modules.shared.alerts.stat_critical')}</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{stats.critical}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600"><Clock className="h-6 w-6 text-white" /></div>
          </div>
          <p className="text-white/60 text-sm font-medium">{t('modules.shared.alerts.stat_high_priority')}</p>
          <p className="text-2xl font-bold text-orange-400 mt-1">{stats.high}</p>
        </motion.div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-white/60" />
            <span className="text-white/60 text-sm">{t('modules.shared.alerts.filters_label')}</span>
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500">
            <option value="all">{t('modules.shared.alerts.filter_all_types')}</option>
            <option value="warning">{t('modules.shared.alerts.filter_warning')}</option>
            <option value="error">{t('modules.shared.alerts.filter_error')}</option>
            <option value="info">{t('modules.shared.alerts.filter_info')}</option>
            <option value="success">{t('modules.shared.alerts.filter_success')}</option>
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500">
            <option value="all">{t('modules.shared.alerts.filter_all_priorities')}</option>
            <option value="critical">{t('modules.shared.alerts.priority_critical')}</option>
            <option value="high">{t('modules.shared.alerts.priority_high')}</option>
            <option value="medium">{t('modules.shared.alerts.priority_medium')}</option>
            <option value="low">{t('modules.shared.alerts.priority_low')}</option>
          </select>
          <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl flex items-center gap-2 border border-white/20 ml-auto">
            <Check size={18} />
            {t('modules.shared.alerts.mark_all_read')}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredAlerts.map((alert, index) => {
          const iconData = getAlertIcon(alert.type);
          const priorityBadge = getPriorityBadge(alert.priority);
          const IconComponent = iconData.icon;

          return (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`bg-white/10 backdrop-blur-lg rounded-3xl p-6 border ${!alert.read ? 'border-white/30' : 'border-white/10'} hover:bg-white/15 transition-colors`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-2xl ${iconData.bg}`}>
                  <IconComponent className={`h-6 w-6 ${iconData.color}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`font-semibold ${!alert.read ? 'text-white' : 'text-white/80'}`}>{alert.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${priorityBadge.color}`}>{priorityBadge.text}</span>
                    {!alert.read && <span className="w-2 h-2 rounded-full bg-blue-400"></span>}
                  </div>
                  <p className="text-white/60 text-sm mb-3">{alert.message}</p>
                  <div className="flex items-center gap-4 text-sm text-white/40">
                    <span className="flex items-center gap-1"><Calendar size={14} />{new Date(alert.date).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    {alert.amount != null && <span className="text-white/60 font-medium">{formatBrandMoney(alert.amount)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"><Eye size={18} /></button>
                  <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"><X size={18} /></button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredAlerts.length === 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
          <Bell size={48} className="mx-auto mb-4 text-white/30" />
          <p className="text-white/60 text-lg">{t('modules.shared.alerts.empty_state')}</p>
        </div>
      )}
    </div>
  );
};

export default ModuleAlertsPage;
